/**
 * TanStack Query hooks for Wish Pool admin
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { wishesApi, type ClusterListParams } from './api';
import type { ClusterUpdateRequest, ClusterMergeRequest } from './types';

export const wishKeys = {
  all: ['wishes'] as const,
  clusters: (params: ClusterListParams) => [...wishKeys.all, 'clusters', params] as const,
  cluster: (id: number) => [...wishKeys.all, 'cluster', id] as const,
};

export function useClusterList(params: ClusterListParams = {}) {
  return useQuery({
    queryKey: wishKeys.clusters(params),
    queryFn: () => wishesApi.listClusters(params),
  });
}

export function useClusterDetail(clusterId: number | null) {
  return useQuery({
    queryKey: wishKeys.cluster(clusterId!),
    queryFn: () => wishesApi.getCluster(clusterId!),
    enabled: clusterId !== null,
  });
}

export function useUpdateCluster() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, update }: { id: number; update: ClusterUpdateRequest }) =>
      wishesApi.updateCluster(id, update),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: wishKeys.all });
      toast.success('已保存');
    },
    onError: (err: Error) => {
      toast.error(`更新失败: ${err.message}`);
    },
  });
}

export function useMergeClusters() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ClusterMergeRequest) => wishesApi.mergeClusters(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: wishKeys.all });
      toast.success('合并成功');
    },
    onError: (err: Error) => {
      toast.error(`合并失败: ${err.message}`);
    },
  });
}

export function useNotifyCluster() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (clusterId: number) => wishesApi.notifyCluster(clusterId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: wishKeys.all });
      toast.success(`通知已发送，队列 ${data.queued_notifications} 条`);
    },
    onError: (err: Error) => {
      toast.error(`通知失败: ${err.message}`);
    },
  });
}

export function useDeleteWish() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (wishId: number) => wishesApi.deleteWish(wishId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: wishKeys.all });
      toast.success('愿望已删除');
    },
    onError: (err: Error) => {
      toast.error(`删除失败: ${err.message}`);
    },
  });
}
